import { mapState } from 'vuex';

import { formMixin } from '@/mixins/form';
import { notificationsMixin } from './notifications';
import { methodsMixin } from './methods';
import { tryParseInt } from '@/utils/number';

/**
 * Mixin zarządzania formularzem edycji/dodawania
 *
 * @export
 * @param {string} moduleName - nazwa modułu
 * @param {Object} messages - treści notyfikacji
 * @param {string} messages.updated - wiadomość po aktualizacji encji
 * @param {string} messages.updateError - wiadomość w przypadku niepowodzenia przy aktualizacji encji
 * @param {string} messages.add - wiadomość po dodaniu encji
 * @param {string} messages.addError - wiadomość w przypadku niepowodzenia przy dodaniu encji
 * @param {string} messages.formError - treść notyfikacji o błędzie walidacji
 * @param {function} emptyModel - funkcja tworząca pusty model
 * @returns
 */
export function formViewMixin(moduleName, messages, emptyModel) {
  return {
    mixins: [formMixin, notificationsMixin, methodsMixin],
    data() {
      return {
        model: emptyModel(),
        id: this.edit ? this.$store.state[moduleName].data.id : undefined
      };
    },
    props: {
      // Czy pobierać dane do formularza
      edit: {
        type: Boolean,
        required: false,
        default: false
      },
      // Czy formularz wykorzystywany jest w stepperze
      stepper: {
        type: Boolean,
        required: false,
        default: false
      }
    },
    methods: {
      // @vuese
      // Funkcja waliduje formularz, i zapisuje/nadpisuje dane w bazie
      save() {
        if (!this.$schema.$validateAll().length) {
          if (this.edit) {
            // Emituje event do edytowania danych
            // @arg Zwalidowany model do edycji w bazie
            this.update({
              id: tryParseInt(this.id, 10),
              ...this.model
            });
          } else {
            // Emituje event do zapisu danych
            // @arg Zwalidowany model do utworzenia w bazie
            this.add(this.model);
          }
        } else {
          this.notifyError(messages.formError || 'messages.form.error');
        }
      },
      cancel() {
        // Zamyka widok formularza
        this.$emit('cancel');
      },

      /**
       * Ustawia dane formularza
       */
      setData() {
        this.$schema.$setData(this.edit ? this.stateData : emptyModel());
        this.$schema.$reset();
      },
      /**
       * Zapisuje encję
       *
       * @param {*} data
       */
      add(data) {
        this.$store
          .dispatch(`${moduleName}/add`, this.deepClone(data))
          .then(id => {
            this.notifySuccess(messages.added);
            this.setData();
            this.$emit('added', id);
          })
          .catch(err => {
            err.message ? this.notifyError(messages.addError, [err.message]) : this.notifyError(messages.addError);
          });
      },
      /**
       * Aktualizuje encję
       *
       * @param {*} data
       */
      update(data) {
        this.$store
          .dispatch(`${moduleName}/update`, this.deepClone(data))
          .then(id => {
            this.notifySuccess(messages.updated);
            this.$emit('updated', id);
          })
          .catch(err => {
            err.message
              ? this.notifyError(messages.updateError, [err.message])
              : this.notifyError(messages.updateError);
          });
      }
    },
    computed: mapState({
      stateData: state => state[moduleName].data
    }),
    mounted() {
      this.setData();
    }
  };
}
